import { ReactNode } from 'react';
import { isObject, isString, startsWith } from 'lodash-es';

import store from 'redux/store';
import { showBanner } from 'redux/actions/global';
import {
  getActivateIssuedCardErrorMessage,
  getCreatePaymentErrorMessage,
  getDefaultErrorMessage,
  getPayoutRequiredFieldsErrorMessage,
  getHostedPageSupportedPMTErrorMessage,
  getInvalidStatusErrorMessage,
  getCustomerSupportError
} from 'utilities';
import { DigitalWalletProvider, REGEXES } from 'consts';
import i18n from 'services/i18n';

type IErrorAlert =
  | string
  | {
      response?: {
        data?: {
          status?: {
            error_code: string;
          };
          data?: Record<string, any>;
        };
      };
    };

const showErrorAlert = (params: {
  error: IErrorAlert;
  isCard?: boolean;
  digitalWalletProvider?: DigitalWalletProvider;
  custom?: boolean;
  errorTitleKey?: string;
}) => {
  const { error, custom, isCard, digitalWalletProvider, errorTitleKey } = params;

  const alert: { title?: string; text: ReactNode } = {
    title: i18n.t(errorTitleKey || ''),
    text: ''
  };

  const { dispatch } = store;

  if (isObject(error) && error.response?.data?.status) {
    const { status } = error.response.data;
    const { error_code: errorCode } = status;
    alert.text = errorText({ error: errorCode, isCard, digitalWalletProvider });
    return dispatch(showBanner({ props: alert }));
  }

  if (custom && typeof error === 'string') {
    alert.text = error;
  } else {
    alert.text = errorText({ error, isCard, digitalWalletProvider });
  }

  dispatch(showBanner({ props: alert }));
};

const errorText = ({
  error: errorCode,
  isCard,
  digitalWalletProvider
}: {
  error: IErrorAlert;
  isCard?: boolean;
  digitalWalletProvider?: DigitalWalletProvider;
}): any => {
  if (isString(errorCode) && startsWith(errorCode, 'INVALID_REQUIRED_FIELD')) {
    return i18n.t('error_required_fields_general');
  }
  if (isCard) {
    return parseCardError({ errorCode, digitalWalletProvider });
  }
  switch (errorCode) {
    case 'ERROR_CREATE_PAYMENT_INSUFFICIENT_FUNDS':
      return i18n.t('errors.checkout.ERROR_CREATE_PAYMENT_INSUFFICIENT_FUNDS');
    case 'ERROR_AUTHENTICATION_EMAIL_UNAVAILABLE':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('errors.checkout.ERROR_AUTHENTICATION_EMAIL_UNAVAILABLE')
      });
    case 'ERROR_CREATE_PAYMENT_MANDATE_NOT_VALID':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('errors.checkout.ERROR_CREATE_PAYMENT_MANDATE_NOT_VALID')
      });
    case 'ERROR_DUPLICATE_TRANSACTION':
      return i18n.t('errors.checkout.ERROR_DUPLICATE_TRANSACTION');
    case 'ERROR_CREATE_PAYMENT_BANK_TRANSFER_MANDATE_INCOMPLETE':
      return getDefaultErrorMessage({
        openingSentence: i18n.t(
          'errors.checkout.ERROR_CREATE_PAYMENT_BANK_TRANSFER_MANDATE_INCOMPLETE'
        )
      });
    case 'ERROR_CREATE_PAYMENT_CONTACT_BANK':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('errors.checkout.ERROR_CREATE_PAYMENT_CONTACT_BANK')
      });
    case 'INVALID_LAST_NAME':
      return i18n.t('errors.last_name_not_valid');
    case 'INVALID_FIRST_NAME':
      return i18n.t('errors.first_name_not_valid');
    case 'INVALID_ADDRESS_NAME':
    case 'INVALID_ADDRESS_LINE1':
    case 'INVALID_ADDRESS_LINE_1':
      return i18n.t('errors.address_line_1_not_valid');
    case 'INVALID_ADDRESS_ZIP':
      return i18n.t('errors.zip_code_not_valid');
    case 'INVALID_CITY':
      return i18n.t('errors.city_not_valid');
    case 'INVALID_PHONE_NUMBER':
      return i18n.t('errorPage500.phone_number_invalid');
    case 'INVALID_EMAIL':
      return i18n.t('email_not_valid');
    case 'ERROR_CREATE_PAYMENT_METHOD':
      return i18n.t('error_general');
    case 'INVALID_STATE':
    case 'INVALID_COUNTRY':
      return i18n.t('error_card_address');
    case 'ERROR_HOSTED_PAGE_PAGE_EXPIRED':
      return i18n.t('session_expires');
    case 'ERROR_CREATE_BENEFICIARY_COUNTRY_NOT_VALID':
      return i18n.t('create_beneficiary_country_invalid');
    case 'ERROR_CREATE_BENEFICIARY_CURRENCY_NOT_VALID':
      return i18n.t('create_beneficiary_currency_invalid');
    case 'ERROR_INVALID_PAYOUT_BENEFICIARY_ENTITY_TYPE':
      return i18n.t('create_beneficiary_entity_type_invalid');
    case 'ERROR_HOSTED_PAGE_SAVE_BENEFICIARY':
      return i18n.t('create_beneficiary_general_error');
    case 'ERROR_CANNOT_SET_FIXED_SIDE_FOR_NON_FX_PAYMENT':
      return i18n.t('error_cannot_create_fx_payment');
    case 'ERROR_DELETE_PAYMENT_METHOD_IN_USE':
    case 'ERROR_DELETE_DEFAULT_PAYMENT_METHOD_IN_USE':
      return i18n.t('subscription.remove_tokenized_card_error');
    case 'INVALID_FIELDS_AMOUNT':
      return i18n.t('errors.checkout.invalid_fields_amount');
    case 'REQUEST_DECLINED':
      return i18n.t('errors.checkout.request_declined');
    case 'ERROR_CREATE_PAYMENT_ADDRESS_VERIFICATION_FAILURE_N':
      return i18n.t(
        'errors.checkout.error_create_payment_address_verification_failure_n'
      );
    case 'ERROR_SET_KBA':
      return i18n.t('activate_issued_card.error_set_kba');
    case 'ERROR_VIEW_PIN':
      return i18n.t('errors.error_view_pin');
    case 'ERROR_CREATE_HOSTED_PAGE_PAYMENT':
      return getCreatePaymentErrorMessage({});
    case 'ERROR_COUPON_WAS_USED_OR_OUT_OF_DATE':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('errors.coupon_not_valid')
      });
    case 'ERROR_GET_PAYOUT_REQUIRED_FIELDS':
      return getPayoutRequiredFieldsErrorMessage();
    case 'ERROR_CREATE_CARD_TO_CARD_PAYMENT_METHOD':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('card_to_card.tokenize_error')
      });
    case 'ERROR_ACTIVATE_ISSUED_CARD':
      return getActivateIssuedCardErrorMessage({});
    case 'ERROR_PERSONALIZE_ISSUED_CARD':
      return getActivateIssuedCardErrorMessage({
        suffix: i18n.t('activate_issued_card.ERROR_PERSONALIZE_ISSUED_CARD')
      });
    case 'ERROR_ISSUED_CARD_ALREADY_ACTIVATED':
      return getActivateIssuedCardErrorMessage({
        suffix: i18n.t('activate_issued_card.ERROR_ISSUED_CARD_ALREADY_ACTIVATED')
      });
    case 'ERROR_ACTIVATE_BLOCKED_CARD':
      return getActivateIssuedCardErrorMessage({
        suffix: i18n.t('activate_issued_card.ERROR_ACTIVATE_BLOCKED_CARD')
      });
    case 'ERROR_HOSTED_PAGE_COUNTRY_CURRENCY_MISMATCH':
      return getHostedPageSupportedPMTErrorMessage();
    case 'ERROR_HOSTED_PAGE_REUSABLE_LINK_REACHED_MAX_USAGE_LIMIT':
      return getDefaultErrorMessage({ openingSentence: i18n.t('reusable.max_usage') });
    case 'ERROR_PAYMENT_LINK_EXCEEDS_MAX_PAYMENTS':
      return getDefaultErrorMessage({
        openingSentence: i18n.t('reusable.ERROR_PAYMENT_LINK_EXCEEDS_MAX_PAYMENTS'),
        customerSupportPart: getCustomerSupportError({
          prefixKey: 'customerSupport.more_info'
        })
      });
    case 'FACE_TOO_CLOSE':
      return i18n.t('idv.liveness_check_errors.FACE_TOO_CLOSE');
    case 'PAPER_COPY':
      return i18n.t('idv.liveness_check_errors.PAPER_COPY');
    case 'TOO_MANY_FACES':
      return i18n.t('idv.liveness_check_errors.TOO_MANY_FACES');
    case 'FACE_CLOSE_TO_BORDER':
    case 'FACE_NOT_FOUND':
    case 'FACE_TOO_SMALL':
    case 'INVALID_IMAGE':
    case 'SCREEN_REPLY':
    case 'UNKNOWN':
      return i18n.t('idv.liveness_check_errors.GENERAL_ERROR');
    case 'ERROR_HOSTED_PAGE_INVALID_STATUS':
      return getInvalidStatusErrorMessage();
    default:
      return getCreatePaymentErrorMessage({ isDefault: true });
  }
};

const parseCardError = ({
  errorCode,
  digitalWalletProvider
}: {
  errorCode: IErrorAlert;
  digitalWalletProvider?: DigitalWalletProvider;
}) => {
  const defaultCardError = () =>
    getCreatePaymentErrorMessage({
      isCard: true,
      digitalWalletProvider,
      isDefault: true
    });

  const cardNetworkErrorCodeRegex = new RegExp(
    REGEXES.RAP_CREATE_PAYMENT_CARD_NETWORK_ERROR_CODES
  );
  if (isString(errorCode) && cardNetworkErrorCodeRegex.test(errorCode)) {
    const cardNetworkErrorCodeMatchArray = errorCode.match(
      REGEXES.RAP_CREATE_PAYMENT_CARD_NETWORK_ERROR_CODES_EXTRACTOR
    );
    const code = cardNetworkErrorCodeMatchArray && cardNetworkErrorCodeMatchArray[1];

    const errorCodeMessageTranslated: { translation: ReactNode } = { translation: null };
    switch (code) {
      case '01':
      case '02':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_different_pm_contact_issuer'
        ) as string;
        break;
      case '03':
      case '58':
        errorCodeMessageTranslated.translation = getDefaultErrorMessage({});
        break;
      case '12':
      case '46':
      case '62':
      case '65':
      case '70':
      case '75':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_different_pm'
        ) as string;
        break;
      case '06':
      case '19':
      case '91':
      case '93':
      case '96':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_error_persists'
        ) as string;
        break;
      case '14':
      case '15':
      case '56':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_correct_cc_number'
        ) as string;
        break;
      case '61':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_missing_funds'
        ) as string;
        break;
      case '54':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_issue_new_card'
        ) as string;
        break;
      case '57':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_authorization'
        ) as string;
        break;
      case '63':
      case 'N7':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_cvv'
        ) as string;
        break;
      case '78':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_cc_blocked'
        ) as string;
        break;
      case '04':
      case '07':
      case '41':
      case '83':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_issuer_resolve'
        ) as string;
        break;
      case '05':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_do_not_honor'
        ) as string;
        break;
      case '43':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_stolen_card'
        ) as string;
        break;
      case '51':
        errorCodeMessageTranslated.translation = i18n.t(
          'createPaymentErrors.card_network_insufficient_funds'
        ) as string;
        break;
      default:
        errorCodeMessageTranslated.translation = defaultCardError();
    }
    return errorCodeMessageTranslated.translation;
  }
  switch (errorCode) {
    case 'ERROR_CREATE_PAYMENT_METHOD':
      return i18n.t('error_card_general');
    case 'ERROR_DELETE_PAYMENT_METHOD_IN_USE':
    case 'ERROR_DELETE_DEFAULT_PAYMENT_METHOD_IN_USE':
      return i18n.t('subscription.remove_tokenized_card_error');
    case 'INVALID_FIELDS_AMOUNT':
      return i18n.t('errors.checkout.invalid_fields_amount');
    case 'REQUEST_DECLINED':
      return i18n.t('errors.checkout.request_declined');
    case 'ERROR_CREATE_PAYMENT_ADDRESS_VERIFICATION_FAILURE_N':
      return i18n.t(
        'errors.checkout.error_create_payment_address_verification_failure_n'
      );
    case 'ERROR_CREATE_HOSTED_PAGE_PAYMENT':
      return getCreatePaymentErrorMessage({ isCard: true, digitalWalletProvider });
    case 'ERROR_CARD_ABSTRACTION_PMT_NOT_FOUND_BY_BIN':
      return i18n.t('card_abstraction_error_description');
    case 'ERROR_CREATE_PAYMENT_GATEWAY_NOT_RESPONDING':
    case 'ERROR_PROCESS_PAYMENT_SETTLEMENT_DECLINED':
    case 'ERROR_PROCESS_PAYMENT_SETTLEMENT_PENDING_ON_GATEWAY':
    case 'ERROR_PROCESS_PAYMENT_PROCESSOR_UNAVAILABLE':
    case 'ERROR_PROCESS_PAYMENT_INSUFFICIENT_FUNDS':
    case 'ERROR_PROCESS_PAYMENT_EXPIRED':
      return i18n.t(`errors.checkout.${errorCode}`);
    case 'ERROR_HOSTED_PAGE_INVALID_STATUS':
      return getInvalidStatusErrorMessage();
    case 'ERROR_COUPON_WAS_USED_OR_OUT_OF_DATE': {
      return getDefaultErrorMessage({
        openingSentence: i18n.t('errors.coupon_not_valid')
      });
    }
    default:
      return defaultCardError();
  }
};

export const parseError = (
  error: Record<string, any>
): {
  status: Record<string, any> | null | undefined;
  data: Record<string, any> | null | undefined;
} => {
  if (!error?.response?.data) return { status: error, data: null };

  return { status: error.response.data.status, data: error.response.data.data };
};

export const parseErrorToSdk = (
  error:
    | string
    | {
        error_code: string;
        operation_id: string;
        [key: string]: any;
      }
) => {
  if (typeof error === 'string') {
    return { error };
  }

  const { error_code: errorCode, operation_id: operationId, ...rest } = error || {};
  return {
    ...rest,
    error: errorCode || '',
    operationId: operationId || ''
  };
};

export default showErrorAlert;
